import { action, makeObservable, observable, runInAction } from 'mobx';
import { api } from 'shared/api/api';
import { LIST_VIEW_SECTIONS } from './listViewSections';
import {
	deferToNextFrame,
	snapshotsEqual,
	stableStringify
} from './viewStateUtils';
import * as pipelineListView from './pipelineListView';
import * as candidatesListView from './candidatesListView';
import * as jobsListView from './jobsListView';
import * as clientsListView from './clientsListView';
import * as externalApplicationsListView from './externalApplicationsListView';

const SECTION_HANDLERS = {
	[LIST_VIEW_SECTIONS.PIPELINE]: {
		captureListView: pipelineListView.capturePipelineListView,
		applyListView: pipelineListView.applyPipelineListView,
		applyFactoryDefaults: pipelineListView.applyPipelineFactoryDefaults,
		resolveViewSnapshot: pipelineListView.resolvePipelineViewSnapshot,
		serializeViewForSave: pipelineListView.serializePipelineViewForSave,
		getFactorySnapshot: pipelineListView.getPipelineFactorySnapshot
	},
	[LIST_VIEW_SECTIONS.CANDIDATES]: {
		captureListView: candidatesListView.captureCandidatesListView,
		applyListView: candidatesListView.applyCandidatesListView,
		applyFactoryDefaults: candidatesListView.applyCandidatesFactoryDefaults,
		resolveViewSnapshot: candidatesListView.resolveCandidatesViewSnapshot,
		serializeViewForSave: candidatesListView.serializeCandidatesViewForSave,
		getFactorySnapshot: candidatesListView.getCandidatesFactorySnapshot
	},
	[LIST_VIEW_SECTIONS.JOBS]: {
		captureListView: jobsListView.captureJobsListView,
		applyListView: jobsListView.applyJobsListView,
		applyFactoryDefaults: jobsListView.applyJobsFactoryDefaults,
		resolveViewSnapshot: jobsListView.resolveJobsViewSnapshot,
		serializeViewForSave: jobsListView.serializeJobsViewForSave,
		getFactorySnapshot: jobsListView.getJobsFactorySnapshot
	},
	[LIST_VIEW_SECTIONS.CLIENTS]: {
		captureListView: clientsListView.captureClientsListView,
		applyListView: clientsListView.applyClientsListView,
		applyFactoryDefaults: clientsListView.applyClientsFactoryDefaults,
		resolveViewSnapshot: clientsListView.resolveClientsViewSnapshot,
		serializeViewForSave: clientsListView.serializeClientsViewForSave,
		getFactorySnapshot: clientsListView.getClientsFactorySnapshot
	},
	[LIST_VIEW_SECTIONS.EXTERNAL_APPLICATIONS]: {
		captureListView:
			externalApplicationsListView.captureExternalApplicationsListView,
		applyListView:
			externalApplicationsListView.applyExternalApplicationsListView,
		applyFactoryDefaults:
			externalApplicationsListView.applyExternalApplicationsFactoryDefaults,
		resolveViewSnapshot:
			externalApplicationsListView.resolveExternalApplicationsViewSnapshot,
		serializeViewForSave:
			externalApplicationsListView.serializeExternalApplicationsViewForSave,
		getFactorySnapshot:
			externalApplicationsListView.getExternalApplicationsFactorySnapshot
	}
};

const NORMALIZE_SNAPSHOT = {
	[LIST_VIEW_SECTIONS.PIPELINE]: pipelineListView.normalizePipelineSnapshot,
	[LIST_VIEW_SECTIONS.CANDIDATES]:
		candidatesListView.normalizeCandidatesSnapshot,
	[LIST_VIEW_SECTIONS.JOBS]: jobsListView.normalizeJobsSnapshot,
	[LIST_VIEW_SECTIONS.CLIENTS]: clientsListView.normalizeClientsSnapshot,
	[LIST_VIEW_SECTIONS.EXTERNAL_APPLICATIONS]:
		externalApplicationsListView.normalizeExternalApplicationsSnapshot
};

class ListViewsStore {
	@observable viewsBySection = {};
	@observable loadingSection = null;

	constructor() {
		makeObservable(this);
	}

	getHandlers(section) {
		return SECTION_HANDLERS[section];
	}

	@action
	async loadSection(section) {
		this.loadingSection = section;
		const result = await api.get(`list-views?section=${section}`);
		runInAction(() => {
			this.loadingSection = null;
			if (result.status === 'success') {
				this.viewsBySection = {
					...this.viewsBySection,
					[section]: result.data
				};
			}
		});
		return result;
	}

	getViews(section) {
		return this.viewsBySection[section] || [];
	}

	getActiveView(section) {
		const views = this.getViews(section);
		return views.find(view => view.isSelected) || views[0] || null;
	}

	getSavedSnapshot(view, section) {
		const handlers = this.getHandlers(section);
		return handlers.resolveViewSnapshot(view);
	}

	isModified(section, currentSnapshot, activeView) {
		if (!activeView) return false;
		const normalize = NORMALIZE_SNAPSHOT[section];
		const savedSnapshot = this.getSavedSnapshot(activeView, section);
		if (!normalize) {
			return !snapshotsEqual(currentSnapshot, savedSnapshot);
		}
		return !snapshotsEqual(
			normalize(currentSnapshot),
			normalize(savedSnapshot)
		);
	}

	@action
	async selectView(section, viewId) {
		const result = await api.patch(`list-views/${viewId}/select`);
		if (result.status === 'success') {
			runInAction(() => {
				const views = this.getViews(section).map(view => ({
					...view,
					isSelected: view.id === viewId
				}));
				this.viewsBySection = {
					...this.viewsBySection,
					[section]: views
				};
			});
		}
		return result;
	}

	@action
	async saveChanges(section, viewId, snapshot) {
		const handlers = this.getHandlers(section);
		const payload = handlers.serializeViewForSave(snapshot);
		const result = await api.put(`list-views/${viewId}`, payload);
		if (result.status === 'success') {
			runInAction(() => {
				const views = this.getViews(section).map(view =>
					view.id === viewId ? result.data : view
				);
				this.viewsBySection = {
					...this.viewsBySection,
					[section]: views
				};
			});
		}
		return result;
	}

	@action
	async saveAsNew(section, name, snapshot) {
		const handlers = this.getHandlers(section);
		const payload = handlers.serializeViewForSave(snapshot);
		const result = await api.post('list-views', {
			section,
			name,
			...payload
		});
		if (result.status === 'success') {
			await this.loadSection(section);
		}
		return result;
	}

	@action
	async deleteView(section, viewId) {
		const result = await api.delete(`list-views/${viewId}`);
		if (result.status === 'success') {
			await this.loadSection(section);
		}
		return result;
	}

	@action
	updateViewInSection(section, updatedView) {
		const views = this.getViews(section).map(view =>
			view.id === updatedView.id ? updatedView : view
		);
		this.viewsBySection = {
			...this.viewsBySection,
			[section]: views
		};
	}
}

export const listViewsStore = new ListViewsStore();

export class ListViewController {
	constructor(section, page) {
		this.section = section;
		this.page = page;
		this.handlers = SECTION_HANDLERS[section];
		this.savedSnapshot = null;
		this.baselineSnapshot = null;
		this.skipModifiedCheck = false;
		this.initialized = false;
		this.cancelled = false;
	}

	cancel() {
		this.cancelled = true;
	}

	captureSnapshot() {
		return this.handlers.captureListView(this.page);
	}

	getViewStateKey() {
		return stableStringify(this.captureSnapshot());
	}

	normalizeSnapshot(snapshot) {
		const normalize = NORMALIZE_SNAPSHOT[this.section];
		return normalize ? normalize(snapshot) : snapshot;
	}

	updateBaseline() {
		this.baselineSnapshot = this.normalizeSnapshot(this.captureSnapshot());
	}

	async initialize() {
		if (listViewsStore.getViews(this.section).length) {
			await this.applyActiveView();
			this.refreshViewsInBackground();
			return;
		}

		const result = await listViewsStore.loadSection(this.section);
		if (this.cancelled) return;

		if (result.status !== 'success') {
			const emptySnapshot = this.handlers.resolveViewSnapshot({
				systemKey: 'personalized',
				filterState: {
					ui: {},
					query: { sort: { column: 'createdAt', asc: false } },
					globalQuery: {}
				},
				columnState: null
			});
			await this.handlers.applyListView(this.page, emptySnapshot);
			if (this.cancelled) return;
			this.initialized = true;
			await deferToNextFrame();
			this.updateBaseline();
			this.page.forceUpdate?.();
			return;
		}

		await this.applyActiveView();
	}

	async applyActiveView() {
		if (this.cancelled) return;

		const activeView = listViewsStore.getActiveView(this.section);

		if (activeView?.systemKey === 'default') {
			await this.handlers.applyFactoryDefaults(this.page);
		} else if (activeView) {
			const snapshot = listViewsStore.getSavedSnapshot(
				activeView,
				this.section
			);
			await this.handlers.applyListView(this.page, snapshot);
		}

		if (this.cancelled) return;

		this.savedSnapshot = activeView
			? listViewsStore.getSavedSnapshot(activeView, this.section)
			: this.handlers.getFactorySnapshot?.() || null;
		this.initialized = true;
		await deferToNextFrame();
		if (this.cancelled) return;
		this.updateBaseline();
		this.page.forceUpdate?.();
	}

	refreshViewsInBackground() {
		const appliedViewId = listViewsStore.getActiveView(this.section)?.id;
		listViewsStore.loadSection(this.section).then(async result => {
			if (this.cancelled || result.status !== 'success') return;

			const activeView = listViewsStore.getActiveView(this.section);
			if (!activeView || activeView.id === appliedViewId) {
				if (activeView) {
					this.savedSnapshot = listViewsStore.getSavedSnapshot(
						activeView,
						this.section
					);
				}
				this.updateBaseline();
				this.page.forceUpdate?.();
				return;
			}

			await this.applyActiveView();
		});
	}

	isModified() {
		if (!this.initialized || this.skipModifiedCheck || !this.baselineSnapshot) {
			return false;
		}
		return !snapshotsEqual(
			this.normalizeSnapshot(this.captureSnapshot()),
			this.baselineSnapshot
		);
	}

	onViewStateChange() {
		if (this.skipModifiedCheck) return;
		this.page.forceUpdate?.();
	}

	async selectView(view) {
		this.skipModifiedCheck = true;

		await listViewsStore.selectView(this.section, view.id);
		await deferToNextFrame();

		const snapshot = listViewsStore.getSavedSnapshot(view, this.section);
		if (view.systemKey === 'default') {
			await this.handlers.applyFactoryDefaults(this.page);
		} else {
			await this.handlers.applyListView(this.page, snapshot);
		}

		this.savedSnapshot = listViewsStore.getSavedSnapshot(
			listViewsStore.getActiveView(this.section),
			this.section
		);
		this.skipModifiedCheck = false;
		await deferToNextFrame();
		this.updateBaseline();
		this.page.forceUpdate?.();
	}

	async saveChanges() {
		const activeView = listViewsStore.getActiveView(this.section);
		if (!activeView || activeView.systemKey === 'default') return;

		const snapshot = this.captureSnapshot();
		const result = await listViewsStore.saveChanges(
			this.section,
			activeView.id,
			snapshot
		);

		if (result.status === 'success') {
			this.savedSnapshot = listViewsStore.getSavedSnapshot(
				listViewsStore.getActiveView(this.section),
				this.section
			);
			this.updateBaseline();
			this.page.forceUpdate?.();
		}

		return result;
	}

	async saveAsNew(name) {
		const snapshot = this.captureSnapshot();
		const result = await listViewsStore.saveAsNew(this.section, name, snapshot);
		if (result.status !== 'success') return result;

		this.savedSnapshot = listViewsStore.getSavedSnapshot(
			listViewsStore.getActiveView(this.section),
			this.section
		);
		this.updateBaseline();
		this.page.forceUpdate?.();
		return result;
	}

	async deleteView(view) {
		if (view.isSystem || view.systemKey) {
			return { status: 'fail', message: 'System views cannot be deleted' };
		}

		const wasActive =
			listViewsStore.getActiveView(this.section)?.id === view.id;
		const result = await listViewsStore.deleteView(this.section, view.id);
		if (result.status !== 'success') return result;

		const fallbackView = result.data?.fallbackView;
		if (wasActive && fallbackView) {
			await this.selectView(fallbackView);
		} else {
			this.page.forceUpdate?.();
		}

		return result;
	}
}
